import { CreateExchangePayload, CreateExchangesPayload, IssuanceReward } from 'app/api/exchange';
import { KameReward } from 'app/api/kame';
import { getProgramsState, ProgramsAppState } from 'app/dux/programs';
import { SessionAppState } from 'app/dux/session';
import { range } from 'lodash';
import { getTokenBuilderState } from 'routes/tokens/create/dux/selectors';
import { TokenBuilderAppState } from './interfaces';
import { isDefined } from 'utils/defined';

function getRewardValue(reward: KameReward): string {
    switch (reward.type) {
        case 'POINTS':
            switch (reward.points.pointsType) {
                case 'FIXED':
                    return reward.points.minValue.toString(10);
                case 'RANGE':
                    return reward.points.minValue.toString(10);
                default:
                    throw new Error('Unsupported points type ' + reward.points.pointsType);
            }
        default:
            throw new Error('Unsupported reward type ' + reward.type);
    }
}

export function buildExchangePayload(this: unknown, state: ProgramsAppState & SessionAppState & TokenBuilderAppState): CreateExchangesPayload {
    const {
        count,
        recipientIds,
        recipients,
        recognitionDistributionsSelected,
    } = getTokenBuilderState(state);

    const {
        recognitionDistributions,
    } = getProgramsState(state);

    const selectedDistributionIds: string[] = Object.entries(recognitionDistributionsSelected)
        .filter(([, selected]) => selected)
        .map(([id]) => id);

    const recipientList = recipientIds.map((recipientId) => recipients[recipientId]).filter(isDefined);

    const exchanges: CreateExchangePayload[] = selectedDistributionIds.flatMap((distributionId) => {
        const distribution = recognitionDistributions?.[distributionId];

        const rewardList = distribution?.rewards.map((reward): IssuanceReward => ({
            recognitionDistributionId: distribution.recognitionDistributionId,
            rewardId: reward.id,
            value: getRewardValue(reward),
        })) ?? [];

        const eligibleGroupIds = new Set(distribution?.rewardDistribution.rosterGroups.map(({ groupId }) => groupId));

        const eligibleRecipients = recipientList.filter((recipient) => (
            recipient.groups.some((group) => eligibleGroupIds.has(group.id))
        ));

        return eligibleRecipients.flatMap((recipient) => (
            range(count).map((): CreateExchangePayload => (
                {
                    personId: recipient.id,
                    rewards: rewardList,
                }
            ))
        ));
    });

    return { exchanges };
}
